import React, { useState } from 'react';
import { reportsAPI } from '../../services/api';
import toast from 'react-hot-toast';

const fmt = (n) => Number(n||0).toLocaleString('en-IN',{minimumFractionDigits:2,maximumFractionDigits:2});

export default function GSTReports() {
  const [tab, setTab] = useState('gstr1');
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [gstr1, setGstr1] = useState(null);
  const [gstr3b, setGstr3b] = useState(null);

  const loadReport = async () => {
    setLoading(true);
    try {
      if (tab === 'gstr1') {
        const r = await reportsAPI.gstr1({ month, year });
        setGstr1(r.data);
      } else {
        const r = await reportsAPI.gstr3b({ month, year });
        setGstr3b(r.data);
      }
    } catch { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  };

  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  return (
    <div>
      <div className="page-header"><div><h1>GST Reports</h1><p className="subtitle">GSTR-1 & GSTR-3B report data</p></div></div>

      <div className="card mb-4">
        <div className="card-body">
          <div className="flex gap-3 items-center" style={{flexWrap:'wrap'}}>
            <div className="tab-bar" style={{marginBottom:0, borderBottom:'none'}}>
              <div className={`tab-item ${tab==='gstr1'?'active':''}`} onClick={() => { setTab('gstr1'); setGstr1(null); }}>GSTR-1</div>
              <div className={`tab-item ${tab==='gstr3b'?'active':''}`} onClick={() => { setTab('gstr3b'); setGstr3b(null); }}>GSTR-3B</div>
            </div>
            <div style={{flex:1}} />
            <select className="form-select" style={{width:'auto'}} value={month} onChange={e => setMonth(Number(e.target.value))}>
              {months.map((m,i) => <option key={i} value={i+1}>{m}</option>)}
            </select>
            <select className="form-select" style={{width:'auto'}} value={year} onChange={e => setYear(Number(e.target.value))}>
              {[2024,2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <button onClick={loadReport} className="btn btn-primary" disabled={loading}>
              {loading ? 'Loading...' : 'Generate Report'}
            </button>
          </div>
        </div>
      </div>

      {/* GSTR-1 */}
      {tab === 'gstr1' && gstr1 && (
        <div>
          <div className="card mb-4">
            <div className="card-header"><h2>B2B Invoices (To Registered Businesses)</h2><span className="badge badge-blue">{gstr1.b2b.length} invoices</span></div>
            <div className="table-container">
              <table>
                <thead><tr><th>Invoice #</th><th>Date</th><th>Customer</th><th>GSTIN</th><th style={{textAlign:'right'}}>Taxable</th><th style={{textAlign:'right'}}>CGST</th><th style={{textAlign:'right'}}>SGST</th><th style={{textAlign:'right'}}>IGST</th><th style={{textAlign:'right'}}>Total</th></tr></thead>
                <tbody>
                  {gstr1.b2b.map((inv,i) => (
                    <tr key={i}>
                      <td className="td-mono font-semibold">{inv.invoice_number}</td>
                      <td className="text-secondary">{new Date(inv.invoice_date).toLocaleDateString('en-IN')}</td>
                      <td>{inv.customer_name}</td>
                      <td className="td-mono">{inv.customer_gstin}</td>
                      <td className="td-amount">₹{fmt(inv.taxable_amount)}</td>
                      <td className="td-amount">₹{fmt(inv.cgst_amount)}</td>
                      <td className="td-amount">₹{fmt(inv.sgst_amount)}</td>
                      <td className="td-amount">₹{fmt(inv.igst_amount)}</td>
                      <td className="td-amount font-bold">₹{fmt(inv.total_amount)}</td>
                    </tr>
                  ))}
                  {gstr1.b2b.length === 0 && <tr><td colSpan={9} className="text-center text-secondary" style={{padding:32}}>No B2B invoices for this period</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-header"><h2>B2C Invoices (To Unregistered)</h2><span className="badge badge-gray">{gstr1.b2c.length} invoices</span></div>
            <div className="table-container">
              <table>
                <thead><tr><th>Invoice #</th><th>Date</th><th>Customer</th><th>Place of Supply</th><th style={{textAlign:'right'}}>Taxable</th><th style={{textAlign:'right'}}>Total</th></tr></thead>
                <tbody>
                  {gstr1.b2c.map((inv,i) => (
                    <tr key={i}>
                      <td className="td-mono">{inv.invoice_number}</td>
                      <td className="text-secondary">{new Date(inv.invoice_date).toLocaleDateString('en-IN')}</td>
                      <td>{inv.customer_name}</td>
                      <td>{inv.place_of_supply || '-'}</td>
                      <td className="td-amount">₹{fmt(inv.taxable_amount)}</td>
                      <td className="td-amount font-bold">₹{fmt(inv.total_amount)}</td>
                    </tr>
                  ))}
                  {gstr1.b2c.length === 0 && <tr><td colSpan={6} className="text-center text-secondary" style={{padding:32}}>No B2C invoices</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-header"><h2>HSN-wise Summary</h2></div>
            <div className="table-container">
              <table>
                <thead><tr><th>HSN Code</th><th>GST Rate</th><th style={{textAlign:'right'}}>Total Qty</th><th style={{textAlign:'right'}}>Taxable Value</th><th style={{textAlign:'right'}}>CGST</th><th style={{textAlign:'right'}}>SGST</th><th style={{textAlign:'right'}}>IGST</th><th style={{textAlign:'right'}}>Total Tax</th></tr></thead>
                <tbody>
                  {gstr1.hsn_summary.map((h,i) => (
                    <tr key={i}>
                      <td className="td-mono font-semibold">{h.hsn_code}</td>
                      <td>{h.gst_rate}%</td>
                      <td className="td-amount">{h.total_qty}</td>
                      <td className="td-amount">₹{fmt(h.taxable_value)}</td>
                      <td className="td-amount">₹{fmt(h.cgst)}</td>
                      <td className="td-amount">₹{fmt(h.sgst)}</td>
                      <td className="td-amount">₹{fmt(h.igst)}</td>
                      <td className="td-amount font-bold">₹{fmt(Number(h.cgst)+Number(h.sgst)+Number(h.igst))}</td>
                    </tr>
                  ))}
                  {gstr1.hsn_summary.length === 0 && <tr><td colSpan={8} className="text-center text-secondary" style={{padding:32}}>No data</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* GSTR-3B */}
      {tab === 'gstr3b' && gstr3b && (
        <div>
          <div className="grid-2">
            <div className="card">
              <div className="card-header"><h2>Outward Supplies (Sales)</h2></div>
              <div className="card-body">
                <div style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid var(--border-light)'}}><span>Taxable Value</span><span className="font-mono font-bold">₹{fmt(gstr3b.outward_supplies.taxable_value)}</span></div>
                <div style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid var(--border-light)'}}><span>IGST</span><span className="font-mono">₹{fmt(gstr3b.outward_supplies.igst)}</span></div>
                <div style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid var(--border-light)'}}><span>CGST</span><span className="font-mono">₹{fmt(gstr3b.outward_supplies.cgst)}</span></div>
                <div style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid var(--border-light)'}}><span>SGST</span><span className="font-mono">₹{fmt(gstr3b.outward_supplies.sgst)}</span></div>
                <div style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid var(--border-light)'}}><span>Cess</span><span className="font-mono">₹{fmt(gstr3b.outward_supplies.cess)}</span></div>
              </div>
            </div>
            <div className="card">
              <div className="card-header"><h2>Input Tax Credit (Purchases)</h2></div>
              <div className="card-body">
                <div style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid var(--border-light)'}}><span>Taxable Value</span><span className="font-mono font-bold">₹{fmt(gstr3b.inward_supplies.taxable_value)}</span></div>
                <div style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid var(--border-light)'}}><span>IGST</span><span className="font-mono">₹{fmt(gstr3b.inward_supplies.igst)}</span></div>
                <div style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid var(--border-light)'}}><span>CGST</span><span className="font-mono">₹{fmt(gstr3b.inward_supplies.cgst)}</span></div>
                <div style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid var(--border-light)'}}><span>SGST</span><span className="font-mono">₹{fmt(gstr3b.inward_supplies.sgst)}</span></div>
              </div>
            </div>
          </div>

          <div className="card" style={{marginTop:16}}>
            <div className="card-header"><h2>Net Tax Liability</h2></div>
            <div className="card-body">
              <div className="stats-grid" style={{gridTemplateColumns:'repeat(4, 1fr)'}}>
                <div className="stat-card"><div className="stat-info"><h3>Net IGST</h3><div className="stat-value" style={{fontSize:20, color: gstr3b.net_tax.igst >= 0 ? 'var(--danger)' : 'var(--success)'}}>₹{fmt(gstr3b.net_tax.igst)}</div></div></div>
                <div className="stat-card"><div className="stat-info"><h3>Net CGST</h3><div className="stat-value" style={{fontSize:20, color: gstr3b.net_tax.cgst >= 0 ? 'var(--danger)' : 'var(--success)'}}>₹{fmt(gstr3b.net_tax.cgst)}</div></div></div>
                <div className="stat-card"><div className="stat-info"><h3>Net SGST</h3><div className="stat-value" style={{fontSize:20, color: gstr3b.net_tax.sgst >= 0 ? 'var(--danger)' : 'var(--success)'}}>₹{fmt(gstr3b.net_tax.sgst)}</div></div></div>
                <div className="stat-card"><div className="stat-info"><h3>Total Payable</h3><div className="stat-value" style={{fontSize:20, color: gstr3b.net_tax.total >= 0 ? 'var(--danger)' : 'var(--success)'}}>₹{fmt(gstr3b.net_tax.total)}</div></div></div>
              </div>
              <p className="text-sm text-secondary" style={{marginTop:12}}>Positive values indicate tax payable to government. Negative values indicate excess ITC available.</p>
            </div>
          </div>
        </div>
      )}

      {!gstr1 && !gstr3b && !loading && (
        <div className="card"><div className="empty-state" style={{padding:60}}>
          <div className="empty-icon">📊</div>
          <h3>Select Period & Generate Report</h3>
          <p>Choose month, year, and report type above, then click Generate.</p>
        </div></div>
      )}
    </div>
  );
}
